import { useTerminology } from '@/lib/useTerminology'
import { useState } from 'react'
import {
  ScanLine, Plus, Trash2, ChevronUp, ChevronDown, FlaskConical,
  CheckCircle2, XCircle, Loader2,
} from 'lucide-react'
import { pageTitle, sectionTitle, cardTitle, formCard, formCardSm, fieldGroup, fieldLabel, inputBase, selectBase, fieldWrapper, pageWide } from '@/lib/form-styles'
import { FeldStatus, FeldStatusZeichen, useAenderung, useEntwurf, useStellenDaten, type Datensatz, type Speicherstelle, type Status } from '@/lib/speichern'
import { useZeitKonfiguration } from '@/lib/zeit-konfiguration'
import { testScanRules } from '../api/time-scan.api'
import {
  SCAN_RULE_MATCH_FIELDS,
  type ScanRule,
  type ScanRuleMode,
  type ScanRuleSegment,
  type ScanRuleTarget,
  type ScanRuleTestResult,
} from '../types/time-scan.types'
import { bestaetige } from '@/lib/dialoge'

/**
 * AP-14-B — Scan-Regeln (ADMIN).
 *
 * Eine Regel beschreibt, wie der QR-Code eines Fremdsystems zerlegt wird, damit
 * *jeder* Auftrag dieses Systems automatisch auflöst. Einzeln gelernte Codes gibt
 * es seit 21.9.2026 nicht mehr — die Regeln sind der einzige Weg.
 *
 * Bewusst KEINE Listenseite nach dem FilterBar-/ColumnLayout-Standard: Das hier
 * sind Einstellungen (typisch 1–3 Regeln je Tenant), keine Datensatzliste —
 * Filter, Spaltenkonfiguration und Pagination hätten nichts zu tun.
 *
 * Das Testfeld unten ist Pflichtbestandteil, kein Extra: Das Codeformat eines
 * fremden Systems ist unbekannt, und ohne sofortige Rückmeldung ließe sich eine
 * Regel nur blind beim Kunden erproben.
 *
 * **AP-36 Phase C: Regel = Gruppe.** Eine gespeicherte Regel wird beim Verlassen
 * ihrer Karte gespeichert, als Operation auf dem aktuellen Stand der Liste,
 * erkannt an ihrer `id` (8.3 a). Reihenfolge und Löschen gehen sofort. Eine neue
 * Regel (auch ein Vorschlag) bleibt ein Entwurf mit „Anlegen" (Fall 2). Den
 * Knopf „Speichern" für die ganze Liste gibt es nicht mehr.
 */

const MODE_LABELS: Record<ScanRuleMode, string> = {
  delimiter: 'Trennzeichen',
  fixed: 'Feste Position',
  regex: 'Muster (Regex)',
}

const TARGET_LABELS: Record<ScanRuleTarget, string> = {
  PROJECT: 'Projekt',
  POSITION: 'Position',
  STEP: 'Arbeitsschritt',
  ACTIVITY: 'Tätigkeit',
}

const MATCH_LABELS: Record<string, string> = {
  externalId: 'Externe ID (Fremdsystem)',
  projectNumber: 'Projektnummer',
  nummer: 'Nummer',
  name: 'Bezeichnung',
}

function newRuleId(): string {
  return `rule-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
}

function emptyRule(priority: number): ScanRule {
  return {
    id: newRuleId(),
    name: '',
    enabled: true,
    priority,
    mode: 'delimiter',
    delimiter: '-',
    segments: [{ target: 'PROJECT', matchOn: 'projectNumber', index: 1 }],
  }
}

/** Die Regeln aus einem Stand der Konfiguration, in Prüfreihenfolge. */
const regelnAus = (daten: Datensatz | undefined): ScanRule[] =>
  Array.isArray(daten?.scanRules) ? [...(daten!.scanRules as ScanRule[])].sort((x, y) => x.priority - y.priority) : []

/** Priorität an der Reihenfolge ausrichten — sie steuert, welche Regel bei mehreren passenden zuerst greift. */
const nummeriert = (regeln: ScanRule[]) => regeln.map((r, i) => ({ ...r, priority: (i + 1) * 10 }))

export default function ScanRulesPage() {
  const { stelle, geladen, fehler } = useZeitKonfiguration()
  if (fehler) {
    return (
      <div className={pageWide}>
        <div className="rounded-md border border-red-400 bg-red-50 p-3 text-sm text-red-700 dark:border-red-600 dark:bg-red-950/40 dark:text-red-300">
          Die Scan-Regeln konnten nicht geladen werden.
        </div>
      </div>
    )
  }
  if (!geladen) return <div className={`${pageWide} text-sm text-muted-foreground`}>Wird geladen …</div>
  return <ScanRegeln stelle={stelle as Speicherstelle<unknown>} />
}

function ScanRegeln({ stelle }: { stelle: Speicherstelle<unknown> }) {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const daten = useStellenDaten(stelle)
  const rules = regelnAus(daten)
  // Neue Regeln sind Entwürfe, bis „Anlegen" sie in die Liste schreibt (Fall 2).
  const [entwuerfe, setEntwuerfe] = useState<ScanRule[]>([])
  const [notice, setNotice] = useState<string | null>(null)

  const addRule = () => {
    setEntwuerfe((prev) => [...prev, emptyRule(0)])
    setNotice(null)
  }

  const entferneEntwurf = (id: string) => setEntwuerfe((prev) => prev.filter((r) => r.id !== id))

  return (
    <div className={pageWide}>
      <header>
        <div className="flex items-center gap-2">
          <ScanLine className="h-6 w-6 text-primary" />
          <h1 className={pageTitle}>Scan-Regeln</h1>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Eine Regel beschreibt, wie der Code eines Fremdsystems aufgebaut ist — welcher Teil
          {` die ${terms.number} ist, welcher die Position, welcher der Arbeitsschritt.`} Ist die Regel
          einmal hinterlegt, lösen <strong>alle</strong> Codes dieses Systems automatisch auf,
          auch nie zuvor gescannte. Ohne Regel muss jeder neue Code einzeln zugeordnet werden.
          Änderungen an einer Regel werden gespeichert, sobald du ihre Karte verlässt.
        </p>
      </header>

      {notice && (
        <div className="flex items-center gap-2 rounded-md border border-green-400 bg-green-50 p-3 text-sm text-green-700 dark:border-green-600 dark:bg-green-950/40 dark:text-green-300">
          <CheckCircle2 className="h-4 w-4" /> {notice}
        </div>
      )}

      {rules.length === 0 && entwuerfe.length === 0 && (
        <div className={formCard}>
          <p className="text-sm text-muted-foreground">
            Es ist noch keine Regel hinterlegt. Ohne Regel erkennt der Auftrags-Scan nur
            selbstbeschreibende Codes, die Codes der Arbeitskarte und Treffer über die externe ID —
            alle übrigen Codes wählt der Mitarbeiter von Hand.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {rules.map((rule, i) => (
          <GespeicherteRegel key={rule.id} stelle={stelle} rule={rule} index={i} isFirst={i === 0} isLast={i === rules.length - 1} />
        ))}
        {entwuerfe.map((rule, i) => (
          <NeueRegel
            key={rule.id}
            stelle={stelle}
            start={rule}
            index={rules.length + i}
            onFertig={() => {
              entferneEntwurf(rule.id)
              setNotice(null)
            }}
            onVerwerfen={() => entferneEntwurf(rule.id)}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={addRule}
          className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:bg-muted"
        >
          <Plus className="h-4 w-4" /> Regel hinzufügen
        </button>
      </div>

      <RuleTester rules={[...rules, ...nummeriert(entwuerfe).map((r) => ({ ...r, priority: r.priority + rules.length * 10 }))]} dirty={entwuerfe.length > 0} />

    </div>
  )
}

/** Eine gespeicherte Regel: Entwurf der Karte, gespeichert beim Verlassen. */
function GespeicherteRegel({ stelle, rule, index, isFirst, isLast }: { stelle: Speicherstelle<unknown>; rule: ScanRule; index: number; isFirst: boolean; isLast: boolean }) {
  const entwurf = useEntwurf<ScanRule>(stelle, {
    kennung: `scanRules:${rule.id}`,
    gespeichert: rule,
    felder: ['scanRules'],
    label: rule.name || 'Scan-Regel',
    operation: (e) => (aktuell) => ({ scanRules: regelnAus(aktuell).map((r) => (r.id === e.id ? { ...e, priority: r.priority } : r)) }),
  })
  const aktion = useAenderung(stelle, `scanRules:${rule.id}:aktion`)
  const move = (delta: number) =>
    void aktion.sende({
      felder: ['scanRules'],
      operation: (aktuell) => {
        const next = regelnAus(aktuell)
        const von = next.findIndex((r) => r.id === rule.id)
        const nach = von + delta
        if (von < 0 || nach < 0 || nach >= next.length) return { scanRules: next }
        const [item] = next.splice(von, 1)
        next.splice(nach, 0, item)
        return { scanRules: nummeriert(next) }
      },
    })
  const remove = async () => {
    if (!(await bestaetige(`Regel „${rule.name || 'ohne Namen'}" löschen?`, { ok: 'Löschen', gefaehrlich: true }))) return
    void aktion.sende({ felder: ['scanRules'], operation: (aktuell) => ({ scanRules: nummeriert(regelnAus(aktuell).filter((r) => r.id !== rule.id)) }) })
  }
  return (
    <div {...entwurf.huelle}>
      <RuleEditor
        rule={entwurf.entwurf ?? rule}
        index={index}
        isFirst={isFirst}
        isLast={isLast}
        onChange={(patch) => entwurf.setze((r) => ({ ...r, ...patch }))}
        onMove={move}
        onRemove={() => void remove()}
        status={entwurf.fehler ? entwurf : aktion.status.zustand !== 'ruhig' ? aktion.status : entwurf}
        unten={entwurf.geaendert && <p className="mt-3 text-xs text-muted-foreground">Wird beim Verlassen der Karte gespeichert. Esc verwirft die Änderungen.</p>}
      />
    </div>
  )
}

/** Eine neue Regel: Entwurf mit „Anlegen" (Fall 2). */
function NeueRegel({ stelle, start, index, onFertig, onVerwerfen }: { stelle: Speicherstelle<unknown>; start: ScanRule; index: number; onFertig: () => void; onVerwerfen: () => void }) {
  const [regel, setRegel] = useState(start)
  const anlegen = useAenderung(stelle, `scanRules:${start.id}:neu`)
  const speichern = async () => {
    const ergebnis = await anlegen.sende({
      felder: ['scanRules'],
      operation: (aktuell) => {
        const jetzt = regelnAus(aktuell)
        return { scanRules: jetzt.some((r) => r.id === regel.id) ? jetzt : nummeriert([...jetzt, regel]) }
      },
    })
    if (ergebnis.art === 'gespeichert') onFertig()
  }
  return (
    <RuleEditor
      rule={regel}
      index={index}
      isFirst
      isLast
      neu
      onChange={(patch) => setRegel((r) => ({ ...r, ...patch }))}
      onMove={() => undefined}
      onRemove={onVerwerfen}
      status={anlegen.status}
      unten={
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => void speichern()}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            {anlegen.status.zustand === 'speichert' && <Loader2 className="h-4 w-4 animate-spin" />}
            Anlegen
          </button>
          <button type="button" onClick={onVerwerfen} className="rounded-md border border-input px-3 py-2 text-sm hover:bg-muted">
            Verwerfen
          </button>
        </div>
      }
    />
  )
}

// ── Editor für eine Regel ─────────────────────────────────────────────────────

function RuleEditor({ rule, index, isFirst, isLast, neu, onChange, onMove, onRemove, status, unten }: {
  rule: ScanRule
  index: number
  isFirst: boolean
  isLast: boolean
  /** Noch nicht angelegt: keine Reihenfolge, Papierkorb verwirft den Entwurf. */
  neu?: boolean
  onChange: (patch: Partial<ScanRule>) => void
  onMove: (delta: number) => void
  onRemove: () => void
  status: Status
  unten?: React.ReactNode
}) {
  const setSegment = (i: number, patch: Partial<ScanRuleSegment>) => {
    onChange({ segments: rule.segments.map((s, j) => (j === i ? { ...s, ...patch } : s)) })
  }

  const addSegment = () => {
    // Die erste noch nicht belegte Ebene vorschlagen — eine Ebene darf nur
    // einmal vorkommen (das prüft auch das Backend).
    const used = new Set(rule.segments.map(s => s.target))
    const free = (Object.keys(SCAN_RULE_MATCH_FIELDS) as ScanRuleTarget[]).find(t => !used.has(t))
    if (!free) return
    onChange({
      segments: [...rule.segments, {
        target: free,
        matchOn: SCAN_RULE_MATCH_FIELDS[free][0],
        ...(rule.mode === 'delimiter' ? { index: rule.segments.length + 1 } : {}),
        ...(rule.mode === 'fixed' ? { from: 0, length: 4 } : {}),
        ...(rule.mode === 'regex' ? { group: '' } : {}),
      }],
    })
  }

  const changeMode = (mode: ScanRuleMode) => {
    // Beim Moduswechsel die modus-spezifischen Felder zurücksetzen, sonst
    // bleiben unsichtbare Altwerte stehen und die Validierung meckert grundlos.
    onChange({
      mode,
      delimiter: mode === 'delimiter' ? (rule.delimiter || '-') : undefined,
      pattern: mode === 'regex' ? (rule.pattern || '') : undefined,
      segments: rule.segments.map((s, i) => ({
        target: s.target,
        matchOn: s.matchOn,
        trim: s.trim,
        stripLeadingZeros: s.stripLeadingZeros,
        upperCase: s.upperCase,
        ...(mode === 'delimiter' ? { index: s.index ?? i + 1 } : {}),
        ...(mode === 'fixed' ? { from: s.from ?? 0, length: s.length ?? 4 } : {}),
        ...(mode === 'regex' ? { group: s.group ?? '' } : {}),
      })),
    })
  }

  return (
    <div className={formCard}>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {neu ? 'neu' : `${index + 1}. geprüft`}
        </span>
        <h2 className={cardTitle}>{rule.name || 'Neue Regel'}</h2>
        <FeldStatusZeichen status={status} />
        {!rule.enabled && (
          <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">inaktiv</span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {!neu && (
          <>
          <button type="button" onClick={() => onMove(-1)} disabled={isFirst}
            title="Früher prüfen"
            className="rounded p-1.5 text-muted-foreground hover:bg-muted disabled:opacity-30">
            <ChevronUp className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={isLast}
            title="Später prüfen"
            className="rounded p-1.5 text-muted-foreground hover:bg-muted disabled:opacity-30">
            <ChevronDown className="h-4 w-4" />
          </button>
          </>
          )}
          <button type="button" onClick={onRemove} title={neu ? 'Entwurf verwerfen' : 'Regel löschen'}
            className="rounded p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <FeldStatus status={status} zeichen={false} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className={fieldWrapper}>
          <label className={fieldLabel}>Name</label>
          <input className={inputBase} value={rule.name}
            placeholder="z.B. BMD Auftragsdeckblatt"
            onChange={e => onChange({ name: e.target.value })} />
        </div>
        <div className={fieldWrapper}>
          <label className={fieldLabel}>Aufbau</label>
          <select className={selectBase} value={rule.mode}
            onChange={e => changeMode(e.target.value as ScanRuleMode)}>
            {(Object.keys(MODE_LABELS) as ScanRuleMode[]).map(m => (
              <option key={m} value={m}>{MODE_LABELS[m]}</option>
            ))}
          </select>
        </div>
        <div className={fieldWrapper}>
          <label className={fieldLabel}>Gilt nur für Codes, die so beginnen</label>
          <input className={inputBase} value={rule.prefix ?? ''}
            placeholder="optional, z.B. AUF-"
            onChange={e => onChange({ prefix: e.target.value || undefined })} />
        </div>
        <div className={fieldWrapper}>
          <label className={fieldLabel}>Aktiv</label>
          <label className="flex h-[42px] items-center gap-2 text-sm">
            <input type="checkbox" checked={rule.enabled}
              onChange={e => onChange({ enabled: e.target.checked })} />
            <span className="text-muted-foreground">Regel wird beim Scannen angewendet</span>
          </label>
        </div>
      </div>

      {rule.mode === 'delimiter' && (
        <div className="mt-3 max-w-xs">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Trennzeichen</label>
            <input className={inputBase} value={rule.delimiter ?? ''} maxLength={4}
              placeholder="z.B. -  ;  |  /"
              onChange={e => onChange({ delimiter: e.target.value })} />
            <p className="text-xs text-muted-foreground">
              Der Code wird an jedem Vorkommen geteilt; die Teile werden von links nach rechts durchnummeriert.
            </p>
          </div>
        </div>
      )}

      {rule.mode === 'regex' && (
        <div className="mt-3">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Muster (regulärer Ausdruck)</label>
            <input className={`${inputBase} font-mono text-sm`} value={rule.pattern ?? ''}
              placeholder="^AUF-(?<projekt>[0-9-]+)-(?<schritt>\d+)$"
              onChange={e => onChange({ pattern: e.target.value })} />
            <p className="text-xs text-muted-foreground">
              Expertenmodus. Jedes Feld unten wird über eine benannte Gruppe <code>(?&lt;name&gt;…)</code> zugeordnet.
            </p>
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className={sectionTitle} style={{ fontSize: '0.95rem' }}>Was steht wo im Code?</h3>
          <button type="button" onClick={addSegment}
            disabled={rule.segments.length >= Object.keys(SCAN_RULE_MATCH_FIELDS).length}
            className="inline-flex items-center gap-1.5 rounded-md border border-input px-2 py-1 text-xs hover:bg-muted disabled:opacity-40">
            <Plus className="h-3.5 w-3.5" /> Feld
          </button>
        </div>

        <div className="space-y-2">
          {rule.segments.map((seg, i) => (
            <div key={i} className={fieldGroup}>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className={fieldWrapper}>
                  <label className={fieldLabel}>Ebene</label>
                  <select className={selectBase} value={seg.target}
                    onChange={e => {
                      const target = e.target.value as ScanRuleTarget
                      // Vergleichsfeld mitziehen — nicht jedes Feld gibt es auf jeder Ebene.
                      const allowed = SCAN_RULE_MATCH_FIELDS[target]
                      setSegment(i, {
                        target,
                        matchOn: allowed.includes(seg.matchOn) ? seg.matchOn : allowed[0],
                      })
                    }}>
                    {(Object.keys(TARGET_LABELS) as ScanRuleTarget[]).map(t => (
                      <option key={t} value={t}>{TARGET_LABELS[t]}</option>
                    ))}
                  </select>
                </div>

                <div className={fieldWrapper}>
                  <label className={fieldLabel}>Wird verglichen mit</label>
                  <select className={selectBase} value={seg.matchOn}
                    onChange={e => setSegment(i, { matchOn: e.target.value })}>
                    {SCAN_RULE_MATCH_FIELDS[seg.target].map(f => (
                      <option key={f} value={f}>{MATCH_LABELS[f] ?? f}</option>
                    ))}
                  </select>
                </div>

                {rule.mode === 'delimiter' && (
                  <div className={fieldWrapper}>
                    <label className={fieldLabel}>Wievielter Teil</label>
                    <input type="number" min={1} className={inputBase} value={seg.index ?? 1}
                      onChange={e => setSegment(i, { index: Number(e.target.value) })} />
                  </div>
                )}

                {rule.mode === 'fixed' && (
                  <>
                    <div className={fieldWrapper}>
                      <label className={fieldLabel}>Ab Zeichen (0 = Anfang)</label>
                      <input type="number" min={0} className={inputBase} value={seg.from ?? 0}
                        onChange={e => setSegment(i, { from: Number(e.target.value) })} />
                    </div>
                    <div className={fieldWrapper}>
                      <label className={fieldLabel}>Länge</label>
                      <input type="number" min={1} className={inputBase} value={seg.length ?? 1}
                        onChange={e => setSegment(i, { length: Number(e.target.value) })} />
                    </div>
                  </>
                )}

                {rule.mode === 'regex' && (
                  <div className={fieldWrapper}>
                    <label className={fieldLabel}>Name der Gruppe</label>
                    <input className={`${inputBase} font-mono text-sm`} value={seg.group ?? ''}
                      placeholder="projekt"
                      onChange={e => setSegment(i, { group: e.target.value })} />
                  </div>
                )}

                <div className="flex items-end gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <input type="checkbox" checked={seg.stripLeadingZeros === true}
                      onChange={e => setSegment(i, { stripLeadingZeros: e.target.checked })} />
                    führende Nullen weg
                  </label>
                  <button type="button"
                    onClick={() => onChange({ segments: rule.segments.filter((_, j) => j !== i) })}
                    disabled={rule.segments.length <= 1}
                    className="ml-auto rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-30 dark:hover:bg-red-950/40"
                    title="Feld entfernen">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {unten}
    </div>
  )
}

// ── Testfeld ──────────────────────────────────────────────────────────────────

function RuleTester({ rules, dirty }: { rules: ScanRule[]; dirty: boolean }) {
  const [rawValue, setRawValue] = useState('')
  const [result, setResult] = useState<ScanRuleTestResult | null>(null)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async () => {
    if (!rawValue.trim()) return
    setTesting(true)
    setError(null)
    try {
      // Immer den aktuellen Editor-Stand testen — sonst prüft der Admin gegen
      // die zuletzt gespeicherte Fassung und wundert sich über das Ergebnis.
      setResult(await testScanRules(rawValue, rules))
    } catch {
      setError('Der Test konnte nicht ausgeführt werden.')
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className={formCard}>
      <div className="mb-3 flex items-center gap-2">
        <FlaskConical className="h-5 w-5 text-primary" />
        <h2 className={sectionTitle}>Regel testen</h2>
      </div>
      <p className="mb-3 max-w-3xl text-sm text-muted-foreground">
        Den Inhalt eines echten QR-Codes hier einfügen. Das Ergebnis zeigt, welche Regel greift,
        was sie aus dem Code herausliest und auf welchen Datensatz das führt.
        {dirty && ' Getestet wird der aktuelle — noch nicht gespeicherte — Stand.'}
      </p>

      <div className="flex flex-wrap gap-2">
        <input
          className={`${inputBase} max-w-xl flex-1 font-mono text-sm`}
          value={rawValue}
          placeholder="z.B. AUF-06239-20-244-3821"
          onChange={e => setRawValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void run() }}
        />
        <button type="button" onClick={run} disabled={testing || !rawValue.trim()}
          className="inline-flex items-center gap-2 rounded-md border border-input px-4 py-2 text-sm hover:bg-muted disabled:opacity-50">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
          Testen
        </button>
      </div>

      {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

      {result && (
        <div className="mt-4 space-y-3">
          {result.matched && result.result ? (
            <div className={formCardSm}>
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Aufgelöst über Regel „{result.result.ruleName || result.result.ruleId}"
              </div>
              <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <ResultRow label="Projekt" value={refText(result.result.projectRef)} />
                <ResultRow label="Position" value={refText(result.result.positionRef)} />
                <ResultRow label="Arbeitsschritt" value={refText(result.result.stepRef)} />
                <ResultRow label="Tätigkeit" value={refText(result.result.activityRef)} />
              </dl>
              {result.result.ambiguous && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  Mindestens ein Teil des Codes passte auf mehrere Datensätze. Diese Ebene bleibt
                  bewusst leer — der Mitarbeiter wählt sie von Hand.
                </p>
              )}
            </div>
          ) : (
            <div className={formCardSm}>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <XCircle className="h-4 w-4" />
                Keine Regel hat diesen Code aufgelöst — der Mitarbeiter müsste ihn einzeln zuordnen.
              </div>
            </div>
          )}

          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Im Einzelnen ({result.rulesEvaluated} Regel(n) geprüft)
            </h3>
            <div className="space-y-2">
              {result.trace.map((t, i) => (
                <div key={`${t.ruleId}-${i}`} className={formCardSm}>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{t.ruleName || t.ruleId}</span>
                    <span className={
                      t.resolves
                        ? 'rounded bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-green-950/60 dark:text-green-300'
                        : 'rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground'
                    }>
                      {t.resolves ? 'greift' : t.applies ? 'passt, löst aber nicht auf' : 'passt nicht'}
                    </span>
                    <span className="text-xs text-muted-foreground">{t.reason}</span>
                  </div>
                  {t.segments.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-xs">
                      {t.segments.map((seg, j) => (
                        <li key={j} className="flex flex-wrap items-center gap-2">
                          {seg.resolved
                            ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            : <XCircle className="h-3.5 w-3.5 text-muted-foreground" />}
                          <span className="text-muted-foreground">{TARGET_LABELS[seg.target]}</span>
                          <span className="font-mono">{seg.value}</span>
                          <span className="text-muted-foreground">
                            → {MATCH_LABELS[seg.matchOn] ?? seg.matchOn}
                            {seg.resolved ? '' : ' (kein Datensatz gefunden)'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              {result.trace.length === 0 && (
                <p className="text-sm text-muted-foreground">Es ist keine Regel hinterlegt.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ResultRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mb-1 sm:mb-0">{value}</dd>
    </>
  )
}

function refText(ref: { name?: string; projectNumber?: string; nummer?: number | null } | null): string {
  if (!ref) return '—'
  if (ref.projectNumber) return `${ref.projectNumber} · ${ref.name ?? ''}`.trim()
  if (ref.nummer != null) return `${ref.nummer} – ${ref.name ?? ''}`.trim()
  return ref.name ?? '—'
}
